// src/lib/notify.js
// Calls your Cloud Function "notifyQueue" (callable) to enqueue emails.

import { getFunctions, httpsCallable } from "firebase/functions";
import "../firebase"; // side-effect import: ensures your Firebase app is initialized

// -----------------------------
// Brand config (edit as needed)
// -----------------------------
const BRAND = {
  name: "You’re Welcome",
  url: "https://yourewelcomeapp.com",
  logoUrl: "https://yourewelcomeapp.com/logo.png", // <— update if you have a different path
  primary: "#7C9082",  // accent green used in app
  dark: "#111111",
  lightBg: "#f7f7f7",
  cardBg: "#ffffff",
  text: "#111111",
  muted: "#555555",
  faint: "#888888",
  supportEmail: "support@yourewelcomeapp.com",
};

// Append simple UTM params for basic attribution in analytics.
function withUtm(href, source = "welcome_email", medium = "email", campaign = "onboarding") {
  try {
    const u = new URL(href);
    // Only set if not already present (prevents double-appending when callers pre-tag)
    if (!u.searchParams.has("utm_source")) u.searchParams.set("utm_source", source);
    if (!u.searchParams.has("utm_medium")) u.searchParams.set("utm_medium", medium);
    if (!u.searchParams.has("utm_campaign")) u.searchParams.set("utm_campaign", campaign);
    return u.toString();
  } catch {
    return href;
  }
}

// ---------------------------------
// Callable setup (same as before)
// ---------------------------------
const functions = getFunctions(undefined, "us-central1");
const callNotifyQueue = httpsCallable(functions, "notifyQueue");

// Low-level sender (unchanged API)
export async function notify({ to, subject, html, text = "", idKey, replyTo = BRAND.supportEmail }) {
  const res = await callNotifyQueue({ to, subject, html, text, idKey, replyTo });
  return res.data; // { ok: true }
}

// ---------------------------------
// Email template primitives
// ---------------------------------

/**
 * Base email shell (600px, table-based, inline styles).
 * Pass a preheader (hidden preview text) and the inner HTML content.
 */
function renderBaseEmail({ preheader = "", innerHtml = "" }) {
  // Hidden preheader improves Gmail/Apple Mail preview lines
  const pre = preheader
    ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">
         ${escapeHtml(preheader)}&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;
       </div>`
    : "";

  return `
  <!doctype html>
  <html lang="en">
  <head>
    <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
    <meta name="viewport" content="width=device-width" />
    <title>${escapeHtml(BRAND.name)}</title>
    <style>
      /* Minimal mobile-friendly tweaks */
      @media only screen and (max-width: 620px) {
        .container { width: 100% !important; }
        .p-24 { padding: 16px !important; }
        .px-24 { padding-left: 16px !important; padding-right: 16px !important; }
        .py-28 { padding-top: 20px !important; padding-bottom: 20px !important; }
        .h1 { font-size: 22px !important; }
      }
      a { text-decoration: none; }
    </style>
  </head>
  <body style="margin:0;padding:0;background:${BRAND.lightBg};">
    ${pre}
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${BRAND.lightBg};padding:24px 12px;">
      <tr>
        <td align="center">
          <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" class="container" style="width:600px;max-width:600px;background:${BRAND.cardBg};border-radius:12px;overflow:hidden;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
            <!-- Header -->
            <tr>
              <td class="px-24" style="padding:18px 24px;background:${BRAND.dark};color:#fff;">
                <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                  <tr>
                    <td style="font-weight:700;font-size:18px;line-height:1.2;">
                      ${BRAND.logoUrl
                        ? `<img src="${BRAND.logoUrl}" height="24" alt="${escapeHtml(BRAND.name)}" style="display:block;height:24px;width:auto;vertical-align:middle;" />`
                        : `<span>${escapeHtml(BRAND.name)}</span>`}
                    </td>
                    <td align="right" style="font-size:12px;opacity:0.9;">
                      <a href="${withUtm(BRAND.url)}" style="color:#ffffff;">${escapeHtml(new URL(BRAND.url).hostname)}</a>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>

            ${innerHtml}

            <!-- Footer -->
            <tr>
              <td class="px-24 py-28" style="padding:14px 24px;background:#fafafa;color:${BRAND.faint};font-size:12px;">
                <div style="margin:4px 0;">© ${new Date().getFullYear()} ${escapeHtml(BRAND.name)}</div>
                <div style="margin:4px 0;">
                  Questions? <a href="mailto:${BRAND.supportEmail}" style="color:${BRAND.faint};text-decoration:underline;">${BRAND.supportEmail}</a>
                </div>
                <div style="margin:4px 0;">
                  <a href="${withUtm(BRAND.url, 'footer', 'email', 'onboarding')}" style="color:${BRAND.faint};text-decoration:underline;">Visit our website</a>
                </div>
              </td>
            </tr>
          </table>

          <!-- Tiny legal line (outside card) -->
          <div style="max-width:600px;margin:12px auto 0 auto;color:#9aa0a6;font-size:11px;font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;">
            This is a transactional email sent by ${escapeHtml(BRAND.name)}.
          </div>
        </td>
      </tr>
    </table>
  </body>
  </html>`;
}

// Updated to allow optional UTM overrides while keeping old usage intact
function button({ href, label, utm } = {}) {
  const tagged = utm
    ? withUtm(href, utm.source, utm.medium, utm.campaign)
    : withUtm(href);
  return `
    <a href="${tagged}"
       style="display:inline-block;background:${BRAND.primary};color:#ffffff;
              padding:12px 18px;border-radius:8px;font-weight:600;">
      ${escapeHtml(label)}
    </a>`;
}

// Updated to allow optional UTM overrides while keeping old usage intact
function secondaryLink({ href, label, utm } = {}) {
  const tagged = utm
    ? withUtm(href, utm.source, utm.medium, utm.campaign)
    : withUtm(href);
  return `
    <a href="${tagged}"
       style="display:inline-block;color:${BRAND.dark};border:1px solid #e5e7eb;
              padding:10px 16px;border-radius:8px;font-weight:600;background:#ffffff;">
      ${escapeHtml(label)}
    </a>`;
}

function featureRow(items) {
  // items: [{emoji, title, text}]
  const cells = items
    .map(
      (it) => `
      <td width="33.333%" class="p-24" style="padding:18px 12px;vertical-align:top;">
        <div style="font-size:24px;line-height:1;">${it.emoji || "✨"}</div>
        <div style="margin-top:8px;font-weight:700;color:${BRAND.text};">${escapeHtml(it.title)}</div>
        <div style="margin-top:6px;color:${BRAND.muted};font-size:14px;line-height:1.6;">${escapeHtml(it.text)}</div>
      </td>`
    )
    .join("");
  return `
    <tr>
      <td class="px-24" style="padding:0 24px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
          <tr>${cells}</tr>
        </table>
      </td>
    </tr>`;
}

function hrSpacer() {
  return `
    <tr>
      <td class="px-24" style="padding:0 24px;">
        <div style="height:1px;background:#eee;margin:20px 0;"></div>
      </td>
    </tr>`;
}

function textBlock({ title, body }) {
  return `
    <tr>
      <td class="px-24 py-28" style="padding:24px;color:${BRAND.text};">
        <h1 class="h1" style="margin:0 0 8px 0;font-size:24px;line-height:1.3;">${escapeHtml(title)}</h1>
        <p style="margin:0;color:${BRAND.muted};font-size:14px;line-height:1.7;">${escapeHtml(body)}</p>
      </td>
    </tr>`;
}

function ctas({ primaryHref, primaryLabel, secondaryHref, secondaryLabel }) {
  return `
    <tr>
      <td class="px-24" style="padding:0 24px 24px 24px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td>${button({ href: primaryHref, label: primaryLabel })}</td>
            ${
              secondaryHref
                ? `<td width="12"></td><td>${secondaryLink({ href: secondaryHref, label: secondaryLabel })}</td>`
                : ""
            }
          </tr>
        </table>
      </td>
    </tr>`;
}

// Basic HTML → plaintext fallback
function htmlToText(html) {
  if (!html) return "";
  return (
    html
      .replace(/<style[\s\S]*?<\/style>/gi, "")
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
}

function escapeHtml(s = "") {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function trunc(s = "", n = 140) {
  const x = String(s || "");
  return x.length > n ? x.slice(0, n - 1) + "…" : x;
}

// ---------------------------------
// Welcome template + helper
// ---------------------------------

export function buildWelcomeEmail(firstName) {
  const subject = "Welcome to You’re Welcome 🎉";

  const hero = `
    <tr>
      <td class="px-24 py-28" style="padding:28px 24px 10px 24px;">
        <div style="background:linear-gradient(135deg, ${BRAND.primary}1A, #ffffff 60%);
                    border:1px solid #eef0ee;border-radius:12px;padding:22px;">
          <div style="font-size:22px;font-weight:800;color:${BRAND.dark};margin:0 0 6px 0;">
            Welcome, ${escapeHtml(firstName || "there")}!
          </div>
          <div style="color:${BRAND.muted};line-height:1.7;font-size:14px;">
            Your account is live. Explore beautiful homes for daytime gatherings—or start hosting and earn.
          </div>
          <div style="margin-top:14px;">
            ${button({ href: `${BRAND.url}/explore`, label: "Browse listings" })}
          </div>
        </div>
      </td>
    </tr>`;

  const body = [
    hero,
    featureRow([
      { emoji: "🏡", title: "Book stunning spaces", text: "Curated homes for offsites, showers, photoshoots, and small events." },
      { emoji: "💸", title: "Host and earn", text: "Open your space for daytime bookings—no overnight guests required." },
      { emoji: "🔒", title: "Secure checkout", text: "Fast, secure payments powered by Stripe. Receipts and support included." },
    ]),
    hrSpacer(),
    textBlock({
      title: "Quick next steps",
      body:
        "1) Explore spaces near you · 2) Save favorites to compare · 3) Ready to host? Create a listing in minutes and set your rules, availability, and payouts.",
    }),
    ctas({
      primaryHref: `${BRAND.url}/explore`,
      primaryLabel: "Explore listings",
      secondaryHref: `${BRAND.url}/host`,
      secondaryLabel: "Start hosting",
    }),
  ].join("");

  const html = renderBaseEmail({
    preheader: "Your account is ready—explore spaces or start hosting today.",
    innerHtml: body,
  });

  const text =
    `Welcome to You’re Welcome!\n\n` +
    `Your account is ready. Explore beautiful homes for daytime gatherings—or start hosting and earn.\n\n` +
    `Explore listings: ${withUtm(`${BRAND.url}/explore`)}\n` +
    `Start hosting: ${withUtm(`${BRAND.url}/host`)}\n` +
    `Website: ${withUtm(BRAND.url)}\n` +
    `Support: ${BRAND.supportEmail}`;

  return { subject, html, text };
}

export async function sendWelcomeEmail({ toEmail, firstName, uid }) {
  const { subject, html, text } = buildWelcomeEmail(firstName);
  return notify({
    to: `${firstName || "there"} <${toEmail}>`,
    subject,
    html,
    text: text || htmlToText(html),
    idKey: `user.welcome|${uid}`,
  });
}

// ---------------------------------
// New message notification (Inbox)
// ---------------------------------

/**
 * Build the "new message" email shown to the other participant.
 * Uses the same shell as the welcome email and includes a message preview.
 */
export function buildNewMessageEmail({
  recipientFirstName,     // e.g. "Alex"
  otherUserName,          // e.g. "Jordan (Host)"
  listingTitle,           // e.g. "Modern Garden House"
  messageSnippet,         // short preview of the message body
  threadUrl,              // absolute URL to /messages?thread=...
}) {
  const subject =
    `New message from ${otherUserName || "a user"} — ` +
    `${listingTitle || BRAND.name}`;

  const safeSnippet = escapeHtml(trunc(messageSnippet || "", 240));

  const preview = `
    <tr>
      <td class="px-24 py-28" style="padding:24px;">
        <div style="font-size:18px;font-weight:800;color:${BRAND.dark};margin:0 0 6px 0;">
          You’ve got a new message
        </div>
        <div style="color:${BRAND.muted};font-size:14px;line-height:1.7;margin-bottom:14px;">
          From <strong style="color:${BRAND.text}">${escapeHtml(otherUserName || "a user")}</strong>
          ${listingTitle ? ` about <strong style="color:${BRAND.text}">${escapeHtml(listingTitle)}</strong>` : ""}.
        </div>

        <div style="border:1px solid #eef0ee;background:#fbfbfb;border-radius:12px;padding:14px;margin-bottom:14px;">
          <div style="color:${BRAND.muted};font-size:12px;margin-bottom:6px;">Message preview</div>
          <div style="color:${BRAND.text};font-size:14px;line-height:1.7;">“${safeSnippet}”</div>
        </div>

        <div style="margin-top:8px;">
          ${button({
            href: threadUrl || `${BRAND.url}/messages`,
            label: "Reply in Messages",
            utm: { source: "message_email", medium: "email", campaign: "inbox" },
          })}
        </div>
      </td>
    </tr>
  `;

  const tips = featureRow([
    { emoji: "✅", title: "Keep it on-platform", text: "Protect your booking and payments by messaging inside You’re Welcome." },
    { emoji: "🔔", title: "Real-time updates", text: "We’ll notify you when there’s a new reply." },
    { emoji: "🔒", title: "Safety first", text: "Never send payments or personal info outside the app." },
  ]);

  const html = renderBaseEmail({
    preheader: `${otherUserName || "Someone"}: ${trunc(messageSnippet || "New message", 90)}`,
    innerHtml: [preview, hrSpacer(), tips].join(""),
  });

  const text =
    `You’ve got a new message from ${otherUserName || "a user"}\n` +
    (listingTitle ? `About: ${listingTitle}\n` : "") +
    (messageSnippet ? `\n"${trunc(messageSnippet, 240)}"\n\n` : `\n`) +
    `Reply: ${withUtm(threadUrl || `${BRAND.url}/messages`, "message_email", "email", "inbox")}\n\n` +
    `${BRAND.name} • ${BRAND.url}`;

  return { subject, html, text };
}

/**
 * Send the "new message" email. Use this right after a chat message is successfully saved.
 * idKey is optional; if you pass a threadId we’ll include a timestamp to reduce duplicates.
 */
export async function sendNewMessageEmail({
  toEmail,
  recipientFirstName = "there",
  otherUserName,
  listingTitle,
  messageSnippet,
  threadUrl,
  threadId, // optional; used only to make idKey more specific
}) {
  if (!toEmail) return { ok: false, reason: "no_recipient" };

  const { subject, html, text } = buildNewMessageEmail({
    recipientFirstName,
    otherUserName,
    listingTitle,
    messageSnippet,
    threadUrl,
  });

  // Unique-ish idKey to avoid accidental dupes if caller retries quickly.
  const idKey = threadId ? `message.new|${threadId}|${Date.now()}` : undefined;

  return notify({
    to: `${recipientFirstName || "there"} <${toEmail}>`,
    subject,
    html,
    text: text || htmlToText(html),
    idKey,
  });
}
